import { creditScores, defaultEndpoint, getAlternateEndpoint } from '../../config/settings.js';
import { capitalizeFirstLetter, getMmdId, getMmdIdBOA, getDealerDRLeaseEnabled } from '../../helpers/index.js';
import { getSelectedPriceValue } from '../../helpers/pricing.js';

export const getCarApiPayment = async (
	vehicle, 
	walletData, 
	downPaymentData, 
	dealerData, 
	authToken, 
	environment, 
	alternatePaymentEndpoint, 
	ePriceData, 
	config
) => {
	const noResult = {
		noResult: true
	};

	if (!vehicle) {
		return noResult;
	}

	const {
		inventoryType,
		msrp,
		odometer,
		vin,
		year
	} = vehicle;

	const {
		downPayment
	} = downPaymentData;

	const {
		creditType,
		leaseMileageRange,
		leaseTerm,
		loanTerm = 48,
		tradeIn
	} = walletData;

	const {
		amountOwed
	} = tradeIn;

	// is lease enabled for dealer
	const isLeaseEnabled = getDealerDRLeaseEnabled();
	
	const enableLeasePayments = isLeaseEnabled && inventoryType === 'new' && !!leaseTerm;

	const requestTypes = enableLeasePayments ? ['Finance', 'Lease'] : ['Finance'];

	const dealerId = config.dealerCode === 'mmdid_boa' ? getMmdIdBOA(vehicle, dealerData) : getMmdId(vehicle, dealerData);

	// yourEPrice is a string with possible values of 'PLEASE_CALL' or '$34,345'
	const ePrice = ePriceData?.yourEPrice && ePriceData.yourEPrice !== 'PLEASE_CALL' ? 
	Number(ePriceData.yourEPrice.replace(/[^0-9.]+/g,'')) : null;

	// Get the price value for the Dealer's chosen price field
	const priceFieldValue = getSelectedPriceValue(vehicle);

	const {
		dealershipPostalCode
	} = dealerData;

	// We can't get a payment without a postal code.
	if (!dealershipPostalCode) {
		return {};
	}

	// Don't request a payment if there's no price.
	if (priceFieldValue === 0) {
		return noResult;
	}

	const tradeInValue = tradeIn.value || 0;
	const totalPayoffValue = amountOwed || 0;

	const payload = {
		dealer: {
			partnerCode: 'MMD',
			websiteDealerId: dealerId,
			inventoryDealerId: dealerId,
			useWebsiteSettings: false
		},
		quoteRequest: {
			vehicles: {
				sellingVehicle: {
					inventoryType: capitalizeFirstLetter(inventoryType),
					odometerMileage: odometer || 0,
					priceCostDetails: {
						originalMSRP: msrp,
						sellingPrice: ePrice || priceFieldValue
					},
					vehicleIdentifier: {
						vin
					},
					vehicleYear: year
				},
				tradeInVehicles: {
					tradeInSummary: {
						totalACV: 0,
						totalAllowance: tradeInValue,
						totalPayoff: totalPayoffValue,
						actualCashValue: tradeInValue
					}
				}
			},
			participants: {
				buyer: {
					primaryAddress: {
						postalCode: dealershipPostalCode.split('-')[0]
					},
					creditScore: creditScores[creditType]
				}
			},
			financialDetails: {
				requestTypes,
				financeRequest: {
					terms: [
						parseInt(loanTerm, 10)
					],
					cashDownPayment: downPayment
				},
				...(enableLeasePayments && {leaseRequest: {
					terms: [leaseTerm],
					annualMiles: leaseMileageRange,
					cashDownPayment: downPayment
				}}),
				calculationPreferences: {
					quoteStrategy: 'lowestMonthlyPayment',
					includeTaxes: false,
					includeIncentives: false
				}
			}
		}
	}

	const endpointUrl = alternatePaymentEndpoint ? getAlternateEndpoint(environment) : defaultEndpoint;


	const headers = {
		'Accept': 'application/json',
		'Content-Type': 'application/x-www-form-urlencoded'
	}

	if (alternatePaymentEndpoint) {
		headers.Authorization = `Basic ${authToken}`;
	}

	// We use this less common body payload format and Content-Type
	// so that Akamai can cache the POST responses.
	const response = await fetch(endpointUrl, {
		method: 'POST',
		body: `vehicleInfo=${encodeURI(JSON.stringify(payload))}`,
		credentials: 'omit',
		headers
	});

	let serviceData;

	try {
		serviceData = await response.json();
	} catch (err) {
		return noResult;
	}

	const { financePayment, leasePayment } = serviceData;

	return {
		financePayment,
		leasePayment
	}
}
